/**
 * Time zone helpers built on Intl, so offsets and summer/winter time always come
 * from the browser's own tz database for the exact instant, never from a stored
 * number. All instants are epoch milliseconds (UTC).
 */
const HOUR = 3600e3;
const fmtCache = new Map();

function fmt(timeZone, opts) {
  const key = timeZone + JSON.stringify(opts);
  if (!fmtCache.has(key)) fmtCache.set(key, new Intl.DateTimeFormat('en-US', { timeZone, ...opts }));
  return fmtCache.get(key);
}

/** UTC offset in minutes of `timeZone` at instant `ms` (e.g. +120 for CEST). */
export function offsetMinutes(timeZone, ms) {
  const part = fmt(timeZone, { timeZoneName: 'longOffset' })
    .formatToParts(ms)
    .find((p) => p.type === 'timeZoneName');
  const m = part && part.value.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
  if (!m) return 0; // "GMT" alone = UTC
  const sign = m[1] === '-' ? -1 : 1;
  return sign * (parseInt(m[2], 10) * 60 + parseInt(m[3] || '0', 10));
}

export function formatOffset(minutes) {
  const sign = minutes < 0 ? '−' : '+';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sign}${h}${m ? ':' + String(m).padStart(2, '0') : ''}`;
}

/** Short zone name like "CEST" or "EDT" when the locale has one, else null. */
export function zoneAbbrev(timeZone, ms) {
  const part = fmt(timeZone, { timeZoneName: 'short' })
    .formatToParts(ms)
    .find((p) => p.type === 'timeZoneName');
  const v = part ? part.value : '';
  return /^GMT|^UTC/.test(v) ? null : v;
}

/** Local wall-clock parts of instant `ms` in `timeZone`. */
export function localParts(timeZone, ms) {
  const parts = fmt(timeZone, {
    hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', weekday: 'short',
  }).formatToParts(ms);
  const get = (t) => parts.find((p) => p.type === t)?.value;
  return {
    y: +get('year'), mo: +get('month'), d: +get('day'),
    h: +get('hour'), mi: +get('minute'), wd: get('weekday'),
    date: `${get('year')}-${get('month')}-${get('day')}`,
  };
}

export const hhmm = (p) => `${String(p.h).padStart(2, '0')}:${String(p.mi).padStart(2, '0')}`;

/** Today's date (YYYY-MM-DD) in a zone. */
export const todayIn = (timeZone) => localParts(timeZone, Date.now()).date;

/**
 * Instant at which the wall clock in `timeZone` reads `dateStr` (YYYY-MM-DD) `time`
 * (HH:MM). Two passes settle the offset, including across a DST change.
 */
export function zonedToUtc(dateStr, time, timeZone) {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [h, mi] = (time || '00:00').split(':').map(Number);
  const wall = Date.UTC(y, mo - 1, d, h, mi);
  let t = wall - offsetMinutes(timeZone, wall) * 60e3;
  t = wall - offsetMinutes(timeZone, t) * 60e3;
  return t;
}

/** Instant of local midnight at the start of `dateStr` in `timeZone`. */
export const dayStart = (dateStr, timeZone) => zonedToUtc(dateStr, '00:00', timeZone);

export function shiftDate(dateStr, days) {
  const d = new Date(`${dateStr}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Is `timeZone` on summer (daylight saving) time at `ms`? True when the offset is
 * above the smaller of its January/July offsets. Zones without DST return null.
 */
export function summerTime(timeZone, ms) {
  const y = new Date(ms).getUTCFullYear();
  const jan = offsetMinutes(timeZone, Date.UTC(y, 0, 15));
  const jul = offsetMinutes(timeZone, Date.UTC(y, 6, 15));
  if (jan === jul) return null;
  return offsetMinutes(timeZone, ms) > Math.min(jan, jul);
}

/** Next offset change after `fromMs` within `days` days, found to the minute. */
export function nextTransition(timeZone, fromMs, days = 60) {
  const start = offsetMinutes(timeZone, fromMs);
  const step = 6 * HOUR;
  for (let t = fromMs + step; t <= fromMs + days * 24 * HOUR; t += step) {
    if (offsetMinutes(timeZone, t) !== start) {
      let lo = t - step;
      let hi = t;
      while (hi - lo > 60e3) {
        const mid = Math.floor((lo + hi) / 2);
        if (offsetMinutes(timeZone, mid) === start) lo = mid; else hi = mid;
      }
      hi = Math.floor(hi / 60e3) * 60e3; // transitions fall on whole minutes
      const to = offsetMinutes(timeZone, hi);
      return { at: hi, from: start, to, diff: to - start };
    }
  }
  return null;
}

/** "Clocks go back 1 h on Sun, Oct 25" style sentence. */
export function describeTransition(timeZone, tr) {
  if (!tr) return null;
  const when = fmt(timeZone, { weekday: 'short', month: 'short', day: 'numeric' }).format(tr.at);
  const amount = Math.abs(tr.diff) % 60 ? `${Math.abs(tr.diff)} min` : `${Math.abs(tr.diff) / 60} h`;
  return `Clocks go ${tr.diff > 0 ? 'forward' : 'back'} ${amount} on ${when}`;
}

export const weekdayDate = (timeZone, ms) =>
  fmt(timeZone, { weekday: 'short', month: 'short', day: 'numeric' }).format(ms);
